import React, { useCallback, useEffect, useState } from 'react'
import Menu from '@mui/material/Menu'
import Drawer from '@mui/material/Drawer'
import Tooltip from '@mui/material/Tooltip'
import CloseIcon from '@mui/icons-material/Close'
import styled from 'styled-components'
import { redirect } from 'utils/paths'
import { ReactComponent as NotificationIcon } from 'assets/images/notification.svg'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchAllNotificationsRoutine,
  fetchUnreadNotificationsCounterRoutine,
  fetchUnseenNotificationsCounterRoutine,
  markAllNotificationsAsReadRoutine,
  markAllNotificationsAsSeenRoutine,
  markNotificationAsReadRoutine
} from 'features/notifications/ducks/actions'
import {
  getAllNotifications,
  getNotificationsListLoaded,
  getUnreadNotificationsCounter
} from 'features/notifications/ducks/selectors'
import NotificationItem from 'features/notifications/components/NotificationItem'
import { isNotNilOrEmpty } from 'utils/ramda'
import { useLocation } from 'react-router'
import { useApiCall } from 'hooks/useApiCall'
import { PRIVATE_PATHS } from 'utils/paths'

const Notifications = () => {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const dispatch = useDispatch()
  const notifications = useSelector(getAllNotifications)
  const unreadCounter = useSelector(getUnreadNotificationsCounter)
  const listLoaded = useSelector(getNotificationsListLoaded)
  const location = useLocation()
  const { pathname } = location
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
  const [localNotifications, setLocalNotifications] = useState([])

  // Initial data loading - only runs once on component mount
  useApiCall(fetchAllNotificationsRoutine, { deps: [] })
  useApiCall(fetchUnseenNotificationsCounterRoutine, { deps: [] })
  useApiCall(fetchUnreadNotificationsCounterRoutine, { deps: [] })

  // Manual fetch function that can be called when menu opens
  useApiCall(fetchAllNotificationsRoutine, {
    deps: [open],
    skipCondition: !open
  })

  useEffect(() => {
    setLocalNotifications(notifications)
  }, [notifications])

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(fetchUnreadNotificationsCounterRoutine())
      dispatch(fetchUnseenNotificationsCounterRoutine())
    }, 30000)
    return () => clearInterval(interval)
  }, [dispatch])

  const markAllAsRead = useCallback(() => {
    dispatch(markAllNotificationsAsReadRoutine())
    dispatch(markAllNotificationsAsSeenRoutine())
    setLocalNotifications(prev =>
      prev.map(notification =>
        notification.readAt
          ? notification
          : { ...notification, readAt: new Date().toISOString() }
      )
    )
  }, [dispatch])

  const markAsRead = useCallback(
    async id => {
      await dispatch(markNotificationAsReadRoutine(id))
      setLocalNotifications(prev =>
        prev.map(notification =>
          notification.id === id && !notification.readAt
            ? { ...notification, readAt: new Date().toISOString() }
            : notification
        )
      )
    },
    [dispatch]
  )

  const openMenu = event => {
    setAnchorEl(event.currentTarget)
    dispatch(fetchUnreadNotificationsCounterRoutine())
    dispatch(markAllNotificationsAsSeenRoutine())
  }

  const handleClose = () => setAnchorEl(null)

  const handleItemClick = (id, bpId, redirectPath) => () => {
    handleClose()
    markAsRead(id)
    const pathToRedirect = redirectPath || (bpId ? `/bp-list/${bpId}` : '/dashboard')

    if (['bp-list', 'favourites', 'drafts'].some(el => pathname.includes(el))) {
      const fullDomain = `${window.location.protocol}//${window.location.hostname}${
        window.location.port ? ':' + window.location.port : ''
      }`
      window.location.href = `${fullDomain}${pathToRedirect}`
    } else {
      redirect(pathToRedirect)
    }
  }
  const badgeLabel = unreadCounter > 99 ? '99+' : unreadCounter

  return (
    <div>
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ style: { borderRadius: '16px 16px 0 0', zIndex: 1500 } }}
        >
          <NotificationsWrapper>
            <HeaderRow>
              <Title>Notifications</Title>
              <CloseButton
                onClick={handleClose}
                aria-label="Close notifications"
              >
                <CloseIcon fontSize="small" />
              </CloseButton>
            </HeaderRow>
            <UnreadWrapper>
              <UnreadCount>
                {listLoaded ? `${unreadCounter} unread` : 'Loading your notifications'}
              </UnreadCount>
              <UnreadActions>
                <MarkAsReadButton
                  onClick={markAllAsRead}
                  $disabled={unreadCounter === 0}
                >
                  mark all as read
                </MarkAsReadButton>
                <PreferencesLink onClick={() => { handleClose(); redirect(PRIVATE_PATHS.notificationPreferences) }}>
                  preferences
                </PreferencesLink>
              </UnreadActions>
            </UnreadWrapper>
            <EntriesWrapper>
              {!listLoaded ? (
                <LoadingRows />
              ) : isNotNilOrEmpty(localNotifications) ? (
              localNotifications.map(notification => (
                <NotificationItem
                  key={notification.id}
                  handleClick={handleItemClick}
                  notification={notification}
                  forceRead={unreadCounter === 0}
                />
              ))
              ) : (
                <EmptyList>You have no notifications yet. We will let you know here when something needs your attention.</EmptyList>
              )}
            </EntriesWrapper>
          </NotificationsWrapper>
        </Drawer>
      ) : (
        <Menu
          keepMounted
          anchorEl={anchorEl}
          style={{
            marginTop: '10px'
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}
          open={open}
          onClose={handleClose}
        >
          <NotificationsWrapper>
            <Title>Notifications</Title>
            <UnreadWrapper>
              <UnreadCount>
                {listLoaded ? `${unreadCounter} unread` : 'Loading your notifications'}
              </UnreadCount>
              <UnreadActions>
                <MarkAsReadButton
                  onClick={markAllAsRead}
                  $disabled={unreadCounter === 0}
                >
                  mark all as read
                </MarkAsReadButton>
                <PreferencesLink onClick={() => { handleClose(); redirect(PRIVATE_PATHS.notificationPreferences) }}>
                  preferences
                </PreferencesLink>
              </UnreadActions>
            </UnreadWrapper>
            <EntriesWrapper>
              {!listLoaded ? (
                <LoadingRows />
              ) : isNotNilOrEmpty(localNotifications) ? (
              localNotifications.map(notification => (
                <NotificationItem
                  key={notification.id}
                  handleClick={handleItemClick}
                  notification={notification}
                  forceRead={unreadCounter === 0}
                />
              ))
              ) : (
                <EmptyList>You have no notifications yet. We will let you know here when something needs your attention.</EmptyList>
              )}
            </EntriesWrapper>
          </NotificationsWrapper>
        </Menu>
      )}

      <Tooltip title='Notifications'>
        <IconWrapper
          as='button'
          type='button'
          $open={open}
          onClick={openMenu}
          aria-label={unreadCounter > 0 ? `Notifications, ${unreadCounter} unread` : 'Notifications'}
          aria-haspopup='true'
          aria-expanded={open}
        >
          <BellWrap>
            <StyledNotificationIcon id='notification-bell-icon' aria-hidden='true' focusable='false' />
            {unreadCounter > 0 && <CountBadge aria-hidden='true'>{badgeLabel}</CountBadge>}
          </BellWrap>
          {open && <IconBackground />}
        </IconWrapper>
      </Tooltip>
    </div>
  )
}

export default Notifications

const NotificationsWrapper = styled.div`
  box-sizing: border-box;
  width: 500px;
  padding: 15px;

  &:focus {
    outline: 0 !important;
  }

  @media (max-width: 768px) {
    width: calc(100vw - 32px);
    max-width: 420px;
    max-height: calc(100vh - 160px);
    padding: 12px 14px;
  }
`

const Title = styled.div`
  box-sizing: border-box;
  width: 100%;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.darkGrey};
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 8px;
  }
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const CloseButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.darkGrey};
  line-height: 0;
`

const UnreadWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    margin-bottom: 6px;
  }
`

const UnreadCount = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.darkGrey};

  span {
    font-weight: bold;
  }

  @media (max-width: 768px) {
    font-size: 11px;
  }
`

const UnreadActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const MarkAsReadButton = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.secondary};
  text-transform: uppercase;
  cursor: pointer;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  @media (max-width: 768px) {
    font-size: 11px;
  }
`

const PreferencesLink = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.secondary};
  }

  @media (max-width: 768px) {
    font-size: 11px;
  }
`

const IconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: none;
  cursor: pointer;
  line-height: 0;

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }

  svg {
    position: relative;
    z-index: ${({ $open }) => ($open ? 1 : 0)};
  }

  @media (max-width: 768px) {
    svg {
      width: 24px;
      height: 24px;
    }
  }
`

const StyledNotificationIcon = styled(NotificationIcon)``

const IconBackground = styled.div`
  position: absolute;
  border-radius: 50%;
  opacity: 0.6;
  background-color: #dfe4f6;
  width: 40px;
  top: -7px;
  left: -9px;
  height: 40px;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    top: -4px;
    left: -6px;
  }
`

const BellWrap = styled.span`
  position: relative;
  display: inline-flex;
  line-height: 0;
`

const CountBadge = styled.span`
  position: absolute;
  top: -10px;
  right: -14px;
  box-sizing: border-box;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border: 2px solid #fff;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  z-index: 2;

  @media (max-width: 480px) {
    top: -9px;
    right: -12px;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    font-size: 10px;
  }
`

const EntriesWrapper = styled.div`
  box-sizing: border-box;
  max-width: 100%;

  @media (max-width: 768px) {
    max-height: calc(100vh - 210px);
    overflow-y: auto;
  }
`

const LoadingRows = () => (
  <div aria-hidden='true'>
    {[0, 1, 2, 3].map(i => (
      <SkeletonRow key={i}>
        <SkeletonDot />
        <div style={{ flex: 1 }}>
          <SkeletonLine $w='80%' />
          <SkeletonLine $w='50%' />
        </div>
      </SkeletonRow>
    ))}
  </div>
)

const SkeletonRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid #eef0f3;
`

const SkeletonDot = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #eef0f3;
  animation: notifPulse 1.4s ease-in-out infinite;

  @keyframes notifPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.55; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const SkeletonLine = styled.div`
  height: 10px;
  width: ${({ $w }) => $w};
  margin-bottom: 8px;
  border-radius: 4px;
  background: #eef0f3;
  animation: notifPulse 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const EmptyList = styled.div`
  font-size: 14px;
  padding: 20px 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[700]};

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 16px 0;
  }
`
